import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lightbulb } from 'lucide-react';
import { apiFetch } from '../apiClient';

// apps/web cannot import @ps-ops-hub/shared (ADR 0019), so these mirror
// packages/shared/src/suggestions.ts — keep the two lists in step. Exported for SuggestionReviews.js,
// so that's the only other copy — not a third one.
export const CATEGORIES = ['General', 'New feature', 'Bug or annoyance', 'Data or report', 'Process'];
export const STATUSES = ['New', 'Under review', 'Planned', 'In progress', 'Done', 'Declined', 'Duplicate'];
// Mirrors SUGGESTION_AREAS in packages/shared.
export const AREAS = [
  'Dashboard & Timeline', 'My Time & Time Reports', 'Resource Planning', 'Project Details & Launch',
  'Project Schedule', 'Project Registers', 'Project Budgets', 'Project Calendar & Links',
  'Business Process Analysis', 'Data Conversion', 'Config, Testing & Training', 'Go-Live & Tickets',
  'Customer Portal', 'Notifications & Help', 'Administration', 'Other',
];
export const OPEN_STATUSES = ['New', 'Under review', 'Planned', 'In progress'];
// The submitter may edit their own suggestion only while it is in triage (mirrors the API rule).
export const EDITABLE_STATUSES = ['New', 'Under review'];
export const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

export const STATUS_BADGE = {
  New: 'badge-blue',
  'Under review': 'badge-yellow',
  Planned: 'badge-violet',
  'In progress': 'badge-blue',
  Done: 'badge-green',
  Declined: 'badge-gray',
  Duplicate: 'badge-gray',
};

function SuggestionModal({ onClose }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [category, setCategory] = React.useState('General');
  const [text, setText] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const [sent, setSent] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      await apiFetch('/suggestions', {
        method: 'POST',
        body: JSON.stringify({ category, text, pageContext: pathname }),
      });
      setText('');
      setSent(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Suggestion box" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Suggestion box</span>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body">
          <form onSubmit={submit}>
            <div className="form-group">
              <label className="form-label" htmlFor="suggestion-category">Category</label>
              <select
                id="suggestion-category"
                className="form-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="suggestion-text">Your suggestion</label>
              <textarea
                id="suggestion-text"
                className="form-input"
                rows={4}
                maxLength={4000}
                placeholder="What would make Pulse better?"
                value={text}
                onChange={(e) => { setText(e.target.value); setSent(false); }}
              />
              <div className="suggestion-hint">
                Everyone can see suggestions and replies on the Review Suggestions screen, and the
                admins get a notification.
              </div>
            </div>
            {submitError && <div className="notice notice-error">{submitError}</div>}
            {sent && !submitError && <div className="notice notice-info">Thanks — your suggestion has been submitted.</div>}
            <button className="btn btn-primary" type="submit" disabled={submitting || text.trim().length < 3}>
              {submitting ? 'Sending…' : 'Submit suggestion'}
            </button>
          </form>

          {/* The popup itself only submits -- every suggestion (yours and everyone else's), the
              triage replies, and the admin controls all moved to their own filterable/sortable
              screen (SuggestionReviews.js) rather than an inline list here. */}
          <div className="modal-section" style={{ textAlign: 'center' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => { onClose(); navigate('/suggestions'); }}
            >
              Review Suggestions →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Header button, same treatment as Help: on every screen, in the same place. Open state is the
// header's, not this component's, because the notification bell also opens this modal.
export default function SuggestionBox({ open, onOpen, onClose }) {
  return (
    <>
      <button
        className="header-help-btn"
        onClick={onOpen}
        title="Suggest an improvement"
        aria-label="Suggestion box"
      >
        <Lightbulb size={17} strokeWidth={1.75} aria-hidden />
        Suggest
      </button>
      {open && <SuggestionModal onClose={onClose} />}
    </>
  );
}
